import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useClickOutside from "../../../../components/Custom_Hooks/useClickOutsSide";
import { useDispatch, useSelector } from "react-redux";
import {
  closeModal,
  contraVoucherCreate,
  contraVoucherUpdate,
  openModal,
  serialNumberList,
  contraLedgerListing,
} from "../../../../redux/actions/action-creator";
import { useJournalVoucher } from "../../../../components/Custom_Hooks/useJournalVoucher";
import InputField from "../../../../components/Input/InputField";
import DropdownLayout_1 from "../../../../components/Dropdown/DropdownLayout_1";
import {
  prepareFormData,
  useFormHandler,
} from "../../../../components/Custom_Hooks/useFormHandler";
import Form from "../../../../components/Form/Form";
import MasterCreationFooter from "../../../../components/Footer/Master_Footer/MasterCreationFooter";
import {
  currencyFormate,
  getDateInFormate,
} from "../../../../components/Date_Formate_Utilitiy/Index";
import { flushSync } from "react-dom";
import { JournalDynamicInputRow } from "./JournalDynamicInputRow";
import LedgerCreate from "../../master/create/accounting_masters/Ledger";

const Journal = ({
  data,
  setData,
  initialState,
  loading,
  isAlter,
  isView,
  isDuplicate,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Input refs for all voucher types -----------------------
  const inputRefs = {
    voucher_type_name: useRef(null),
    serial_no: useRef(null),
    create_date: useRef(null),
    narration: useRef(null),
  };

  const dynamicInputRef = useRef({});

  const [nameInput, setNameInput] = useState(null);
  const [contra_account_ledger_list, setcontra_account_ledger_list] = useState([]);

  const modal_state = useSelector((state) => state.common.modal_state);
  const { userData } = useSelector((state) => state.auth);

  const {
    handleChange,
    handleFocus,
    handleBlur,
    handleKeyDown,
    setSubmitConfirm,
    submitConfirm,
    handleItemUpdate,
    handleDynamicFocus,
    handleDropdownClick,
    handleDropdownDynamicClick,
    handleDynamicBlur,
  } = useJournalVoucher({
    data,
    setData,
    contra_account_ledger_list: contra_account_ledger_list || [],
    validations: {},
    inputRefs: inputRefs,
    dynamicInputRef: dynamicInputRef,
    setNameInput,
    isView: isView,
    isAlter: isAlter,
  });

  useEffect(() => {
    if (loading) return;

    const isManual = data.method_of_voucher_number?.toLowerCase() === "manual";
    const targetRef = isManual ? inputRefs?.serial_no : inputRefs?.create_date;

    const focusTimer = setTimeout(() => {
      if (targetRef?.current) {
        targetRef.current.focus();
        if (targetRef.current.select) {
          targetRef.current.select();
        } else if (
          targetRef.current.setSelectionRange &&
          targetRef.current.value
        ) {
          targetRef.current.setSelectionRange(
            0,
            targetRef.current.value.length
          );
        }
        setNameInput(targetRef);
      }
    }, 200);

    return () => clearTimeout(focusTimer);
  }, [data?.method_of_voucher_number, data?.voucher_type_name, loading]);

  useEffect(() => {
    API_Function();
  }, [modal_state?.ledgerCreate]);

  // Fetch ledger list
  const API_Function = useCallback(async () => {
    if (userData?.id) {
      try {
        const formdata = new FormData();
        formdata.append("company_id", userData?.id);
        // Use all relevant group IDs for Journal safely supporting both Array and Object structures
        const groups = Array.isArray(userData?.group_data)
          ? userData.group_data
          : userData?.group_data
            ? Object.values(userData.group_data)
            : [];
        const group_ids = groups.map(g => g?.id).filter(Boolean).join(",") || "";
        formdata.append("group_ids", group_ids);
        const response = await dispatch(contraLedgerListing(formdata));
        if (response?.status) {
          setcontra_account_ledger_list(response.data);
        }
      } catch (error) {
        console.log(error);
      }
    }
  }, [userData?.id, userData?.group_data, dispatch]);

  const inputFields = [
    {
      label: "",
      name: "serial_no",
      type: "text",
      isRequired: true,
      isDisabled: true,
    },
    {
      label: "Date",
      name: "create_date",
      type: "text",
      isRequired: true,
      isDisabled: false,
    },
    {
      label: "Narration",
      name: "narration",
      type: "text",
      isRequired: true,
      isDisabled: false,
    },
  ];

  const validateForm = () => {
    //console.log("validateForm", data);
    // Filter out empty rows (those without a ledger selected)
    const validRows = data.data.filter(row => row.particular_ledger_id && row.particular_ledger_id !== "");

    if (validRows.length < 2) {
      toast.error("At least two entries (one Dr and one Cr) are required");
      return false;
    }

    // Check if balanced
    const diff = Math.abs((data.total_debit || 0) - (data.total_credit || 0));
    if (diff > 0.01) {
      toast.error(`Voucher is not balanced. Difference: ${currencyFormate(diff)}`);
      return false;
    }

    return true;
  };

  const resetForm = useCallback(async () => {
    setSubmitConfirm("");

    const fieldsToPreserve = [
      "period_key",
      "create_date",
      "voucher_type_name",
      "voucher_type_id",
      "select_type_of_voucher",
      "method_of_voucher_number",
      "day_name",
    ];

    // Helper function to build reset state
    const buildResetState = (prev) => ({
      ...initialState,
      company_id: userData?.id,
      data: initialState.data,
      // Preserve specified fields from previous state
      ...fieldsToPreserve.reduce((acc, field) => {
        acc[field] = prev?.[field] ?? "";
        return acc;
      }, {}),
    });
    setData((prev) => buildResetState(prev));
  }, [setData, userData?.id, initialState]);

  const { handleSubmit, loading: submitting, handleFocusReset } = useFormHandler({
    validateForm,
    prepareFormData: () => {
      const filteredData = {
        ...data,
        data: data.data
          .filter(row => row.particular_ledger_id && row.particular_ledger_id !== "")
          .map((row, index) => ({
            ...row,
            credit_debit: row.credit_debit || (index === 0 ? "Dr" : "")
          }))
      };
      return prepareFormData(filteredData);
    },
    submitForm: (formData) => dispatch(isAlter ? contraVoucherUpdate(formData) : contraVoucherCreate(formData)),
    shortcuts: [
      {
        key: "a",
        ctrlKey: true,
        action: () => handleSubmit(new Event("submit")),
      },
    ],
    resetForm: (isAlter || isView) ? () => navigate(-1) : resetForm,
    first_ref: inputRefs?.create_date,
    closeModal: () => {
      navigate(-1);
    },
    setSubmitConfirm,
    submitConfirm,
    setData,
    data,
    isView: isView,
    isAlter: isAlter
  });

  const TableHeader = () => {
    return (
      <div className="row p-0 m-0 mb-1 border_2_2px_top border_2_1px_bottom  heading_dyn_creation_data">
        <div
          className="col-1 p-0 m-0 pe-1 font_size font-weight-bold "
          style={{ textAlign: "right" }}
        ></div>
        <div
          className="col-4 p-0 m-0 ps-2 font_size font-weight-bold"
          style={{ textAlign: "left" }}
        >
          Particulars
        </div>
        <div
          className="col-3 p-0 m-0 font_size font-weight-bold"
          style={{ textAlign: "left" }}
        ></div>
        <div
          className="col-2 p-0 m-0 pe-1 font_size font-weight-bold "
          style={{ textAlign: "right" }}
        >
          Debit
        </div>
        <div
          className="col-2 p-0 m-0 pe-1 font_size font-weight-bold "
          style={{ textAlign: "right" }}
        >
          Credit
        </div>
      </div>
    );
  };

  const isCreateMasterEnabled = Object.keys(modal_state || {}).some(
    (key) => modal_state[key] && key.startsWith("first")
  );

  return (
    <>
      <Form
      handleSubmit={handleSubmit}
      handleKeyDown={handleKeyDown}
      submitConfirm={submitConfirm}
      handleFocusReset={handleFocusReset}
      title={`${data?.voucher_type_name} Voucher ${isAlter ? "Alter" : isView ? "View" : (isDuplicate ? "Duplication" : "Creation")}`}
      loading={submitting}
      col="col-12"
      Footer={MasterCreationFooter}
      createMasterEnabled={isCreateMasterEnabled}
      isAlter={isAlter}
      isView={isView}
    >
      <div className="row p-0 m-0 mb-1">
        <div className="col-6">
          <div className="d-flex align-items-center" style={{ height: '3vh' }}>
            <label className="lab_voucher_entry" style={{ whiteSpace: 'nowrap', flexShrink: 0, width: 'auto' }}>
              {data?.voucher_type_name} No
            </label>
            <label style={{ flexShrink: 0, margin: '0 3px' }}>:</label>
            {inputFields
              .slice(0, 1)
              .map(
                (
                  { label, name, type, isRequired, isDisabled, autoFocus },
                  index
                ) => (
                  <input
                    style={{ fontSize: 13, fontWeight: "bold", width: "25%" }}
                    type={type}
                    name={name}
                    id={name}
                    key={name}
                    value={data[name] || ""}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    isRequired={isRequired}
                    disabled={isView || data?.method_of_voucher_number?.toLowerCase() !== "manual"}
                    ref={inputRefs[name]}
                  />
                )
              )}
          </div>
        </div>
        <div className="col-6">
          <div className="row justify-content-end">
            <div className="col-6 d-flex justify-content-end">
              {inputFields
                .slice(1, 2)
                .map(({ label, name, type, isRequired, autoFocus }, index) => (
                  <React.Fragment key={name}>
                    <label className="pe-2">{data?.day_name}</label>
                    <input
                      style={{
                        fontSize: 13,
                        textAlign: "right",
                        fontWeight: "bold",
                        width: "38%",
                        paddingRight: "2px",
                      }}
                      type={type}
                      name={name}
                      value={data[name] || ""}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      onKeyUp={handleChange}
                      onBlur={handleBlur}
                      onFocus={handleFocus}
                      isRequired={isRequired}
                      ref={inputRefs[name]}
                      autoFocus={autoFocus}
                      disabled={isView}
                    />
                  </React.Fragment>
                ))}
            </div>
          </div>
        </div>
      </div>
      <TableHeader />
      <div
        className="row p-0 m-0"
        style={{ height: "67.7vh", overflowY: "scroll" }}
      >
        <div className="col-12 p-0 m-0">
          <JournalDynamicInputRow
            setValueLoop={setData}
            valueLoop={data}
            handleChange={handleItemUpdate}
            handleFocus={handleDynamicFocus}
            handleBlur={handleDynamicBlur}
            handleDropdownDynamicClick={handleDropdownDynamicClick}
            handleKeyDown={handleKeyDown}
            dropList={contra_account_ledger_list}
            inputRefs={dynamicInputRef}
            narr_ref={inputRefs}
            isView={isView}
          />
        </div>
      </div>

      <div className="row p-0 m-0" style={{ width: "100%" }}>
        <div className="col-6 m-0 p-0 ps-1 text-start  border-dark">
          {inputFields
            .slice(2, 3)
            .map(({ label, name, type, isRequired }, index) => (
              <React.Fragment key={name}>
                <label>{label} </label>
                <div className="d-flex justify-content-between align-item-center">
                  <textarea
                    style={{ height: "7.5vh" }}
                    key={index}
                    lable={label}
                    className="narration_input"
                    type={type}
                    name={name}
                    value={data[name] || ""}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleChange}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    isRequired={isRequired}
                    ref={inputRefs[name]}
                    disabled={isView}
                  />
                </div>
              </React.Fragment>
            ))}
        </div>
        <div className="col-6 m-0 p-0">
          <div className="row p-0 m-0">
            <div className="col-8" style={{ width: '36%' }} />
            <div
              className="col-2 pe-1 lab_voucher_entry align-self-end text-end border_2_2px_top border_2_1px_bottom"
              style={{ height: 19.5, width: '32%' }}
            >
              {data?.total_debit ? currencyFormate(data.total_debit) : ""}
            </div>
            <div
              className="col-2 pe-1 lab_voucher_entry align-self-end text-end border_2_2px_top border_2_1px_bottom"
              style={{ height: 19.5, width: '32%' }}
            >
              {data?.total_credit ? currencyFormate(data.total_credit) : ""}
            </div>
          </div>
        </div>
      </div>
    </Form>
      {modal_state?.ledgerCreate && (
        <LedgerCreate
          title="Ledger Creation (Secondary)"
          closeModal_sec={() => dispatch(closeModal("ledgerCreate"))}
        />
      )}
    </>
  );
};

export default Journal;


